import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { Skeleton } from '@/shared/ui/Skeleton'
import { AlertModal } from '@/shared/ui/AlertModal'
import PlansTable from '../components/PlansTable'
import PlansHomeControl from '../components/PlansHomeControl'
import { listPlans, deletePlan } from '../services/planService'
import PlanFormModal from '../components/PlanFormModal'
import type { Plan } from '../types'

export default function PlansPage() {
  const { user } = useAuth()
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [items, setItems] = React.useState<Plan[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const pageSize = 25
  const [search, setSearch] = React.useState('')
  const [openForm, setOpenForm] = React.useState(false)
  const [current, setCurrent] = React.useState<Plan | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { items, total } = await listPlans({ search, page, pageSize })
      setItems(items)
      setTotal(total)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar planos')
    } finally {
      setLoading(false)
    }
  }, [search, page])

  React.useEffect(() => { load() }, [load])

  function onCreate() { setCurrent(null); setOpenForm(true) }
  function onEdit(p: Plan) { setCurrent(p); setOpenForm(true) }
  function onDelete(p?: Plan) { if (p) setCurrent(p); setConfirmDeleteOpen(true) }

  async function handleConfirmDelete() {
    if (!current) return
    try {
      await deletePlan(current.id, true)
      await load()
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao excluir plano')
    } finally {
      setConfirmDeleteOpen(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <PlanFormModal open={openForm} onClose={()=> setOpenForm(false)} onSaved={()=> load()} initial={current ?? undefined} />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Planos</h1>
        <p className="text-sm text-foreground/70">Gerencie os planos de assinatura e visibilidade na Home.</p>
      </div>

      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          {Array.from({ length: 6 }).map((_, i) => (<Skeleton key={i} className="h-12 w-full" />))}
        </div>
      ) : (
        <>
          <PlansHomeControl plans={items} onSaved={load} />
          <div className="mt-8" />
          <PlansTable
            items={items}
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            search={search}
            onSearchChange={(v)=>{ setPage(1); setSearch(v) }}
            onCreate={onCreate}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </>
      )}

      <AlertModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Excluir plano"
        variant="error"
        description={'Esta ação desativará o plano e arquivará o produto no Stripe (se configurado).'}
        confirmText="Excluir"
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

